import { __classPrivateFieldGet, __classPrivateFieldSet, __esDecorate, __runInitializers } from "tslib";
import { LitElement, css, html } from "lit";
import { IsShadowQuality, IsToneMapping, } from "./viewerBase";
import { property, query, state } from "lit/decorators.js";
import { ref } from "lit/directives/ref.js";
import { AsyncLock } from "core/Misc/asyncLock";
import { AbortError } from "core/Misc/error";
import { Logger } from "core/Misc/logger";
// Icon SVG is pulled from https://iconcloud.design
const playFilledIcon = "M5 5.27368C5 3.56682 6.82609 2.48151 8.32538 3.2973L20.687 10.0235C22.2531 10.8756 22.2531 13.124 20.687 13.9762L8.32538 20.7024C6.82609 21.5181 5 20.4328 5 18.726V5.27368Z";
const pauseFilledIcon = "M5.74609 3C4.7796 3 3.99609 3.7835 3.99609 4.75V19.25C3.99609 20.2165 4.7796 21 5.74609 21H9.24609C10.2126 21 10.9961 20.2165 10.9961 19.25V4.75C10.9961 3.7835 10.2126 3 9.24609 3H5.74609ZM14.7461 3C13.7796 3 12.9961 3.7835 12.9961 4.75V19.25C12.9961 20.2165 13.7796 21 14.7461 21H18.2461C19.2126 21 19.9961 20.2165 19.9961 19.25V4.75C19.9961 3.7835 19.2126 3 18.2461 3H14.7461Z";
const arrowResetFilledIcon = "M7.20711 2.54289C7.59763 2.93342 7.59763 3.56658 7.20711 3.95711L5.41421 5.75H13.25C17.6683 5.75 21.25 9.33172 21.25 13.75C21.25 18.1683 17.6683 21.75 13.25 21.75C8.83172 21.75 5.25 18.1683 5.25 13.75C5.25 13.1977 5.69772 12.75 6.25 12.75C6.80228 12.75 7.25 13.1977 7.25 13.75C7.25 17.0637 9.93629 19.75 13.25 19.75C16.5637 19.75 19.25 17.0637 19.25 13.75C19.25 10.4363 16.5637 7.75 13.25 7.75H5.41421L7.20711 9.54289C7.59763 9.93342 7.59763 10.5666 7.20711 10.9571C6.81658 11.3476 6.18342 11.3476 5.79289 10.9571L2.29289 7.45711C1.90237 7.06658 1.90237 6.43342 2.29289 6.04289L5.79289 2.54289C6.18342 2.15237 6.81658 2.15237 7.20711 2.54289Z";
const targetFilledIcon = "M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14ZM6 12C6 8.68629 8.68629 6 12 6C15.3137 6 18 8.68629 18 12C18 15.3137 15.3137 18 12 18C8.68629 18 6 15.3137 6 12ZM12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4Z";
const arrowClockwiseFilledIcon = "M5 12C5 8.13401 8.13401 5 12 5C13.32 5 14.5542 5.36484 15.608 6H15C14.4477 6 14 6.44772 14 7C14 7.55228 14.4477 8 15 8H18C18.5523 8 19 7.55228 19 7C19 6 19 5 19 4C19 3.44772 18.5523 3 18 3C17.4477 3 17 3.44772 17 4V4.51575C15.5702 3.5588 13.85 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C16.9706 21 21 16.9706 21 12C21 11.6199 20.9764 11.2448 20.9304 10.8763C20.8621 10.3282 20.3624 9.93935 19.8144 10.0077C19.2663 10.076 18.8775 10.5757 18.9458 11.1237C18.9815 11.4104 19 11.7028 19 12C19 15.866 15.866 19 12 19C8.13401 19 5 15.866 5 12Z";
const allowedAnimationSpeeds = [0.5, 1, 1.5, 2];
// Converts any standard html color string to an IColor4Like object.
export function parseColor(color) {
    if (!color) {
        return null;
    }
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 1;
    const context = canvas.getContext("2d");
    if (!context) {
        throw new Error("Unable to get 2d context for parseColor");
    }
    context.clearRect(0, 0, 1, 1);
    context.fillStyle = color;
    context.fillRect(0, 0, 1, 1);
    const data = context.getImageData(0, 0, 1, 1).data;
    return { r: data[0] / 255, g: data[1] / 255, b: data[2] / 255, a: data[3] / 255 };
}
function colorToHex(color) {
    const toHex = (v) => Math.round(v * 255)
        .toString(16)
        .padStart(2, "0");
    return `#${toHex(color.r)}${toHex(color.g)}${toHex(color.b)}${toHex(color.a)}`;
}
export function coerceNumericAttribute(value) {
    return value == null ? null : Number(value);
}
export function coerceCameraOrbitOrTarget(value) {
    if (!value) {
        return null;
    }
    const array = value.trim().split(/\s+/);
    if (array.length !== 3) {
        throw new Error(`Camera orbit and target should be defined as three space separated numbers, but was specified as "${value}".`);
    }
    return array.map((value) => Number(value));
}
export function coerceToneMapping(value) {
    if (!value || !IsToneMapping(value)) {
        return null;
    }
    return value;
}
export function coerceShadowQuality(value) {
    if (!value || !IsShadowQuality(value)) {
        return null;
    }
    return value;
}
function coerceResetMode(value) {
    if (!value || value === "auto") {
        return "auto";
    }
    if (value === "reframe") {
        return "reframe";
    }
    return value.trim().split(/\s+/);
}
/**
 * Abstract base class for viewer custom elements.
 * Contains all shared UI logic and depends only on IViewer.
 */
let ViewerElementBase = (() => {
    var _a, _ViewerElementBase__isFaultedBacking_accessor_storage, _ViewerElementBase_renderWhenIdle_accessor_storage, _ViewerElementBase_source_accessor_storage, _ViewerElementBase_extension_accessor_storage, _ViewerElementBase_useOpenPBR_accessor_storage, _ViewerElementBase_environmentLighting_accessor_storage, _ViewerElementBase_environmentSkybox_accessor_storage, _ViewerElementBase_environmentIntensity_accessor_storage, _ViewerElementBase_environmentRotation_accessor_storage, _ViewerElementBase_shadowQuality_accessor_storage, _ViewerElementBase__loadingProgress_accessor_storage, _ViewerElementBase_skyboxBlur_accessor_storage, _ViewerElementBase_toneMapping_accessor_storage, _ViewerElementBase_contrast_accessor_storage, _ViewerElementBase_exposure_accessor_storage, _ViewerElementBase_ssao_accessor_storage, _ViewerElementBase_clearColor_accessor_storage, _ViewerElementBase_cameraAutoOrbit_accessor_storage, _ViewerElementBase_cameraAutoOrbitSpeed_accessor_storage, _ViewerElementBase_cameraAutoOrbitDelay_accessor_storage, _ViewerElementBase_hotSpots_accessor_storage, _ViewerElementBase_animationAutoPlay_accessor_storage, _ViewerElementBase_selectedAnimation_accessor_storage, _ViewerElementBase_animationSpeed_accessor_storage, _ViewerElementBase_animationProgress_accessor_storage, _ViewerElementBase__animations_accessor_storage, _ViewerElementBase__isAnimationPlaying_accessor_storage, _ViewerElementBase__showAnimationSlider_accessor_storage, _ViewerElementBase_selectedMaterialVariant_accessor_storage, _ViewerElementBase_camerasAsHotSpots_accessor_storage, _ViewerElementBase_resetMode_accessor_storage, _ViewerElementBase__canvasContainer_accessor_storage, _ViewerElementBase__hotSpotSelect_accessor_storage;
    let _classSuper = LitElement;
    let _instanceExtraInitializers = [];
    let __isFaultedBacking_decorators;
    let __isFaultedBacking_initializers = [];
    let __isFaultedBacking_extraInitializers = [];
    let _renderWhenIdle_decorators;
    let _renderWhenIdle_initializers = [];
    let _renderWhenIdle_extraInitializers = [];
    let _source_decorators;
    let _source_initializers = [];
    let _source_extraInitializers = [];
    let _extension_decorators;
    let _extension_initializers = [];
    let _extension_extraInitializers = [];
    let _useOpenPBR_decorators;
    let _useOpenPBR_initializers = [];
    let _useOpenPBR_extraInitializers = [];
    let _set_environment_decorators;
    let _environmentLighting_decorators;
    let _environmentLighting_initializers = [];
    let _environmentLighting_extraInitializers = [];
    let _environmentSkybox_decorators;
    let _environmentSkybox_initializers = [];
    let _environmentSkybox_extraInitializers = [];
    let _environmentIntensity_decorators;
    let _environmentIntensity_initializers = [];
    let _environmentIntensity_extraInitializers = [];
    let _environmentRotation_decorators;
    let _environmentRotation_initializers = [];
    let _environmentRotation_extraInitializers = [];
    let _shadowQuality_decorators;
    let _shadowQuality_initializers = [];
    let _shadowQuality_extraInitializers = [];
    let __loadingProgress_decorators;
    let __loadingProgress_initializers = [];
    let __loadingProgress_extraInitializers = [];
    let _skyboxBlur_decorators;
    let _skyboxBlur_initializers = [];
    let _skyboxBlur_extraInitializers = [];
    let _toneMapping_decorators;
    let _toneMapping_initializers = [];
    let _toneMapping_extraInitializers = [];
    let _contrast_decorators;
    let _contrast_initializers = [];
    let _contrast_extraInitializers = [];
    let _exposure_decorators;
    let _exposure_initializers = [];
    let _exposure_extraInitializers = [];
    let _ssao_decorators;
    let _ssao_initializers = [];
    let _ssao_extraInitializers = [];
    let _clearColor_decorators;
    let _clearColor_initializers = [];
    let _clearColor_extraInitializers = [];
    let _cameraAutoOrbit_decorators;
    let _cameraAutoOrbit_initializers = [];
    let _cameraAutoOrbit_extraInitializers = [];
    let _cameraAutoOrbitSpeed_decorators;
    let _cameraAutoOrbitSpeed_initializers = [];
    let _cameraAutoOrbitSpeed_extraInitializers = [];
    let _cameraAutoOrbitDelay_decorators;
    let _cameraAutoOrbitDelay_initializers = [];
    let _cameraAutoOrbitDelay_extraInitializers = [];
    let _hotSpots_decorators;
    let _hotSpots_initializers = [];
    let _hotSpots_extraInitializers = [];
    let _animationAutoPlay_decorators;
    let _animationAutoPlay_initializers = [];
    let _animationAutoPlay_extraInitializers = [];
    let _selectedAnimation_decorators;
    let _selectedAnimation_initializers = [];
    let _selectedAnimation_extraInitializers = [];
    let _animationSpeed_decorators;
    let _animationSpeed_initializers = [];
    let _animationSpeed_extraInitializers = [];
    let _animationProgress_decorators;
    let _animationProgress_initializers = [];
    let _animationProgress_extraInitializers = [];
    let __animations_decorators;
    let __animations_initializers = [];
    let __animations_extraInitializers = [];
    let __isAnimationPlaying_decorators;
    let __isAnimationPlaying_initializers = [];
    let __isAnimationPlaying_extraInitializers = [];
    let __showAnimationSlider_decorators;
    let __showAnimationSlider_initializers = [];
    let __showAnimationSlider_extraInitializers = [];
    let _selectedMaterialVariant_decorators;
    let _selectedMaterialVariant_initializers = [];
    let _selectedMaterialVariant_extraInitializers = [];
    let _camerasAsHotSpots_decorators;
    let _camerasAsHotSpots_initializers = [];
    let _camerasAsHotSpots_extraInitializers = [];
    let _resetMode_decorators;
    let _resetMode_initializers = [];
    let _resetMode_extraInitializers = [];
    let __canvasContainer_decorators;
    let __canvasContainer_initializers = [];
    let __canvasContainer_extraInitializers = [];
    let __hotSpotSelect_decorators;
    let __hotSpotSelect_initializers = [];
    let __hotSpotSelect_extraInitializers = [];
    return _a = class ViewerElementBase extends _classSuper {
            /**
             * Creates an instance of a ViewerElementBase subclass.
             * @param _options The options to use when creating the Viewer.
             */
            constructor(_options = {}) {
                super();
                this._options = (__runInitializers(this, _instanceExtraInitializers), _options);
                this._viewerLock = new AsyncLock();
                this._animationSliderResizeObserver = null;
                // Bindings for properties that are synchronized both ways between the lower level Viewer and the ViewerElementBase.
                this._propertyBindings = [
                    this._createPropertyBinding("clearColor", (viewer) => viewer.onClearColorChanged, (viewer) => (viewer.clearColor = this.clearColor ?? { r: 0, g: 0, b: 0, a: 0 }), (viewer) => (this.clearColor = viewer.clearColor)),
                    this._createPropertyBinding("skyboxBlur", (viewer) => viewer.onEnvironmentConfigurationChanged, (viewer) => (viewer.environmentConfig = { blur: this.skyboxBlur ?? viewer.environmentConfig.blur }), (viewer) => (this.skyboxBlur = viewer.environmentConfig.blur)),
                    this._createPropertyBinding("environmentIntensity", (viewer) => viewer.onEnvironmentConfigurationChanged, (viewer) => (viewer.environmentConfig = { intensity: this.environmentIntensity ?? viewer.environmentConfig.intensity }), (viewer) => (this.environmentIntensity = viewer.environmentConfig.intensity)),
                    this._createPropertyBinding("environmentRotation", (viewer) => viewer.onEnvironmentConfigurationChanged, (viewer) => (viewer.environmentConfig = { rotation: this.environmentRotation ?? viewer.environmentConfig.rotation }), (viewer) => (this.environmentRotation = viewer.environmentConfig.rotation)),
                    this._createPropertyBinding("toneMapping", (viewer) => viewer.onPostProcessingChanged, (viewer) => {
                        if (this.toneMapping) {
                            viewer.postProcessing = { toneMapping: this.toneMapping };
                        }
                    }, (viewer) => (this.toneMapping = viewer.postProcessing?.toneMapping)),
                    this._createPropertyBinding("contrast", (viewer) => viewer.onPostProcessingChanged, (viewer) => (viewer.postProcessing = { contrast: this.contrast ?? undefined }), (viewer) => (this.contrast = viewer.postProcessing.contrast)),
                    this._createPropertyBinding("exposure", (viewer) => viewer.onPostProcessingChanged, (viewer) => (viewer.postProcessing = { exposure: this.exposure ?? undefined }), (viewer) => (this.exposure = viewer.postProcessing.exposure)),
                    this._createPropertyBinding("ssao", (viewer) => viewer.onPostProcessingChanged, (viewer) => (viewer.postProcessing = { ssao: this.ssao ?? undefined }), (viewer) => (this.ssao = viewer.postProcessing.ssao)),
                    this._createPropertyBinding("cameraAutoOrbit", (viewer) => viewer.onCameraAutoOrbitChanged, (viewer) => (viewer.cameraAutoOrbit = { enabled: this.cameraAutoOrbit }), (viewer) => (this.cameraAutoOrbit = viewer.cameraAutoOrbit.enabled)),
                    this._createPropertyBinding("cameraAutoOrbitSpeed", (viewer) => viewer.onCameraAutoOrbitChanged, (viewer) => (viewer.cameraAutoOrbit = { speed: this.cameraAutoOrbitSpeed ?? undefined }), (viewer) => (this.cameraAutoOrbitSpeed = viewer.cameraAutoOrbit.speed)),
                    this._createPropertyBinding("cameraAutoOrbitDelay", (viewer) => viewer.onCameraAutoOrbitChanged, (viewer) => (viewer.cameraAutoOrbit = { delay: this.cameraAutoOrbitDelay ?? undefined }), (viewer) => (this.cameraAutoOrbitDelay = viewer.cameraAutoOrbit.delay)),
                    this._createPropertyBinding("animationSpeed", (viewer) => viewer.onAnimationSpeedChanged, (viewer) => (viewer.animationSpeed = this.animationSpeed), (viewer) => {
                        let speed = viewer.animationSpeed;
                        speed = allowedAnimationSpeeds.reduce((prev, curr) => (Math.abs(curr - speed) < Math.abs(prev - speed) ? curr : prev));
                        this.animationSpeed = speed;
                        this._dispatchCustomEvent("animationspeedchange", (type) => new Event(type));
                    }),
                    this._createPropertyBinding("selectedAnimation", (viewer) => viewer.onSelectedAnimationChanged, (viewer) => (viewer.selectedAnimation = this.selectedAnimation ?? viewer.selectedAnimation), (viewer) => (this.selectedAnimation = viewer.selectedAnimation)),
                    this._createPropertyBinding("selectedMaterialVariant", (viewer) => viewer.onSelectedMaterialVariantChanged, (viewer) => (viewer.selectedMaterialVariant = this.selectedMaterialVariant ?? viewer.selectedMaterialVariant ?? ""), (viewer) => (this.selectedMaterialVariant = viewer.selectedMaterialVariant)),
                    this._createPropertyBinding("hotSpots", (viewer) => viewer.onHotSpotsChanged, (viewer) => (viewer.hotSpots = this.hotSpots ?? viewer.hotSpots), (viewer) => (this.hotSpots = viewer.hotSpots)),
                    this._createPropertyBinding("camerasAsHotSpots", (viewer) => viewer.onCamerasAsHotSpotsChanged, (viewer) => (viewer.camerasAsHotSpots = this.camerasAsHotSpots ?? viewer.camerasAsHotSpots), (viewer) => (this.camerasAsHotSpots = viewer.camerasAsHotSpots)),
                ];
                _ViewerElementBase__isFaultedBacking_accessor_storage.set(this, __runInitializers(this, __isFaultedBacking_initializers, false));
                _ViewerElementBase_renderWhenIdle_accessor_storage.set(this, (__runInitializers(this, __isFaultedBacking_extraInitializers), __runInitializers(this, _renderWhenIdle_initializers, this._options.autoSuspendRendering === false)));
                _ViewerElementBase_source_accessor_storage.set(this, (__runInitializers(this, _renderWhenIdle_extraInitializers), __runInitializers(this, _source_initializers, this._options.source ?? null)));
                _ViewerElementBase_extension_accessor_storage.set(this, (__runInitializers(this, _source_extraInitializers), __runInitializers(this, _extension_initializers, null)));
                _ViewerElementBase_useOpenPBR_accessor_storage.set(this, (__runInitializers(this, _extension_extraInitializers), __runInitializers(this, _useOpenPBR_initializers, this._options.useOpenPBR ?? false)));
                _ViewerElementBase_environmentLighting_accessor_storage.set(this, (__runInitializers(this, _useOpenPBR_extraInitializers), __runInitializers(this, _environmentLighting_initializers, this._options.environmentLighting ?? null)));
                _ViewerElementBase_environmentSkybox_accessor_storage.set(this, (__runInitializers(this, _environmentLighting_extraInitializers), __runInitializers(this, _environmentSkybox_initializers, this._options.environmentSkybox ?? null)));
                _ViewerElementBase_environmentIntensity_accessor_storage.set(this, (__runInitializers(this, _environmentSkybox_extraInitializers), __runInitializers(this, _environmentIntensity_initializers, this._options.environmentConfig?.intensity ?? null)));
                _ViewerElementBase_environmentRotation_accessor_storage.set(this, (__runInitializers(this, _environmentIntensity_extraInitializers), __runInitializers(this, _environmentRotation_initializers, this._options.environmentConfig?.rotation ?? null)));
                _ViewerElementBase_shadowQuality_accessor_storage.set(this, (__runInitializers(this, _environmentRotation_extraInitializers), __runInitializers(this, _shadowQuality_initializers, null)));
                _ViewerElementBase__loadingProgress_accessor_storage.set(this, (__runInitializers(this, _shadowQuality_extraInitializers), __runInitializers(this, __loadingProgress_initializers, false)));
                _ViewerElementBase_skyboxBlur_accessor_storage.set(this, (__runInitializers(this, __loadingProgress_extraInitializers), __runInitializers(this, _skyboxBlur_initializers, this._options.environmentConfig?.blur ?? null)));
                _ViewerElementBase_toneMapping_accessor_storage.set(this, (__runInitializers(this, _skyboxBlur_extraInitializers), __runInitializers(this, _toneMapping_initializers, this._options.postProcessing?.toneMapping ?? null)));
                _ViewerElementBase_contrast_accessor_storage.set(this, (__runInitializers(this, _toneMapping_extraInitializers), __runInitializers(this, _contrast_initializers, this._options.postProcessing?.contrast ?? null)));
                _ViewerElementBase_exposure_accessor_storage.set(this, (__runInitializers(this, _contrast_extraInitializers), __runInitializers(this, _exposure_initializers, this._options.postProcessing?.exposure ?? null)));
                _ViewerElementBase_ssao_accessor_storage.set(this, (__runInitializers(this, _exposure_extraInitializers), __runInitializers(this, _ssao_initializers, this._options.postProcessing?.ssao ?? null)));
                _ViewerElementBase_clearColor_accessor_storage.set(this, (__runInitializers(this, _ssao_extraInitializers), __runInitializers(this, _clearColor_initializers, this._options.clearColor
                    ? { r: this._options.clearColor[0], g: this._options.clearColor[1], b: this._options.clearColor[2], a: this._options.clearColor[3] ?? 1 }
                    : null)));
                _ViewerElementBase_cameraAutoOrbit_accessor_storage.set(this, (__runInitializers(this, _clearColor_extraInitializers), __runInitializers(this, _cameraAutoOrbit_initializers, this._options.cameraAutoOrbit?.enabled ?? false)));
                _ViewerElementBase_cameraAutoOrbitSpeed_accessor_storage.set(this, (__runInitializers(this, _cameraAutoOrbit_extraInitializers), __runInitializers(this, _cameraAutoOrbitSpeed_initializers, this._options.cameraAutoOrbit?.speed ?? null)));
                _ViewerElementBase_cameraAutoOrbitDelay_accessor_storage.set(this, (__runInitializers(this, _cameraAutoOrbitSpeed_extraInitializers), __runInitializers(this, _cameraAutoOrbitDelay_initializers, this._options.cameraAutoOrbit?.delay ?? null)));
                _ViewerElementBase_hotSpots_accessor_storage.set(this, (__runInitializers(this, _cameraAutoOrbitDelay_extraInitializers), __runInitializers(this, _hotSpots_initializers, this._options.hotSpots ?? {})));
                _ViewerElementBase_animationAutoPlay_accessor_storage.set(this, (__runInitializers(this, _hotSpots_extraInitializers), __runInitializers(this, _animationAutoPlay_initializers, !!this._options.animationAutoPlay)));
                _ViewerElementBase_selectedAnimation_accessor_storage.set(this, (__runInitializers(this, _animationAutoPlay_extraInitializers), __runInitializers(this, _selectedAnimation_initializers, this._options.selectedAnimation ?? null)));
                _ViewerElementBase_animationSpeed_accessor_storage.set(this, (__runInitializers(this, _selectedAnimation_extraInitializers), __runInitializers(this, _animationSpeed_initializers, this._options.animationSpeed ?? 1)));
                _ViewerElementBase_animationProgress_accessor_storage.set(this, (__runInitializers(this, _animationSpeed_extraInitializers), __runInitializers(this, _animationProgress_initializers, 0)));
                _ViewerElementBase__animations_accessor_storage.set(this, (__runInitializers(this, _animationProgress_extraInitializers), __runInitializers(this, __animations_initializers, [])));
                _ViewerElementBase__isAnimationPlaying_accessor_storage.set(this, (__runInitializers(this, __animations_extraInitializers), __runInitializers(this, __isAnimationPlaying_initializers, false)));
                _ViewerElementBase__showAnimationSlider_accessor_storage.set(this, (__runInitializers(this, __isAnimationPlaying_extraInitializers), __runInitializers(this, __showAnimationSlider_initializers, true)));
                _ViewerElementBase_selectedMaterialVariant_accessor_storage.set(this, (__runInitializers(this, __showAnimationSlider_extraInitializers), __runInitializers(this, _selectedMaterialVariant_initializers, this._options.selectedMaterialVariant ?? null)));
                _ViewerElementBase_camerasAsHotSpots_accessor_storage.set(this, (__runInitializers(this, _selectedMaterialVariant_extraInitializers), __runInitializers(this, _camerasAsHotSpots_initializers, false)));
                _ViewerElementBase_resetMode_accessor_storage.set(this, (__runInitializers(this, _camerasAsHotSpots_extraInitializers), __runInitializers(this, _resetMode_initializers, "auto")));
                _ViewerElementBase__canvasContainer_accessor_storage.set(this, (__runInitializers(this, _resetMode_extraInitializers), __runInitializers(this, __canvasContainer_initializers, void 0)));
                _ViewerElementBase__hotSpotSelect_accessor_storage.set(this, (__runInitializers(this, __canvasContainer_extraInitializers), __runInitializers(this, __hotSpotSelect_initializers, void 0)));
                __runInitializers(this, __hotSpotSelect_extraInitializers);
                this._options = _options;
            }
            /** @internal */
            // eslint-disable-next-line @typescript-eslint/naming-convention
            static get observedAttributes() {
                // These attributes don't have corresponding properties, so they are managed directly.
                return [...super.observedAttributes, "camera-orbit", "camera-target"];
            }
            /**
             * Get hotspot world and screen values from a named hotspot
             * @param name slot of the hot spot
             * @param result resulting world and screen positions
             * @returns world position, world normal and screen space coordinates
             */
            queryHotSpot(name, result) {
                if (this._viewer) {
                    return this._viewer.queryHotSpot(name, result);
                }
                return false;
            }
            /**
             * Updates the camera to focus on a named hotspot.
             * @param name The name of the hotspot to focus on.
             * @returns true if the hotspot was found and the camera was updated, false otherwise.
             */
            focusHotSpot(name) {
                if (this._viewer) {
                    return this._viewer.focusHotSpot(name);
                }
                return false;
            }
            get _isFaultedBacking() { return __classPrivateFieldGet(this, _ViewerElementBase__isFaultedBacking_accessor_storage, "f"); }
            set _isFaultedBacking(value) { __classPrivateFieldSet(this, _ViewerElementBase__isFaultedBacking_accessor_storage, value, "f"); }
            get _isFaulted() {
                return this._isFaultedBacking;
            }
            /**
             * When true, the scene will be rendered even if no scene state has changed.
             */
            get renderWhenIdle() { return __classPrivateFieldGet(this, _ViewerElementBase_renderWhenIdle_accessor_storage, "f"); }
            set renderWhenIdle(value) { __classPrivateFieldSet(this, _ViewerElementBase_renderWhenIdle_accessor_storage, value, "f"); }
            /**
             * The model URL.
             */
            get source() { return __classPrivateFieldGet(this, _ViewerElementBase_source_accessor_storage, "f"); }
            set source(value) { __classPrivateFieldSet(this, _ViewerElementBase_source_accessor_storage, value, "f"); }
            /**
             * Forces the model to be loaded with the specified extension.
             * @remarks
             * If this property is not set, the extension will be inferred from the model URL when possible.
             */
            get extension() { return __classPrivateFieldGet(this, _ViewerElementBase_extension_accessor_storage, "f"); }
            set extension(value) { __classPrivateFieldSet(this, _ViewerElementBase_extension_accessor_storage, value, "f"); }
            /**
             * If true, load glTF files using the OpenPBR material instead of the default PBR material.
             * @experimental
             */
            get useOpenPBR() { return __classPrivateFieldGet(this, _ViewerElementBase_useOpenPBR_accessor_storage, "f"); }
            set useOpenPBR(value) { __classPrivateFieldSet(this, _ViewerElementBase_useOpenPBR_accessor_storage, value, "f"); }
            /**
             * The texture URLs used for lighting and skybox. Setting this property will set both environmentLighting and environmentSkybox.
             */
            get environment() {
                return { lighting: this.environmentLighting, skybox: this.environmentSkybox };
            }
            set environment(url) {
                this.environmentLighting = url || null;
                this.environmentSkybox = url || null;
            }
            /**
             * The texture URL for lighting.
             */
            get environmentLighting() { return __classPrivateFieldGet(this, _ViewerElementBase_environmentLighting_accessor_storage, "f"); }
            set environmentLighting(value) { __classPrivateFieldSet(this, _ViewerElementBase_environmentLighting_accessor_storage, value, "f"); }
            /**
             * The texture URL for the skybox.
             */
            get environmentSkybox() { return __classPrivateFieldGet(this, _ViewerElementBase_environmentSkybox_accessor_storage, "f"); }
            set environmentSkybox(value) { __classPrivateFieldSet(this, _ViewerElementBase_environmentSkybox_accessor_storage, value, "f"); }
            /**
             * A value between 0 and 2 that specifies the intensity of the environment lighting.
             */
            get environmentIntensity() { return __classPrivateFieldGet(this, _ViewerElementBase_environmentIntensity_accessor_storage, "f"); }
            set environmentIntensity(value) { __classPrivateFieldSet(this, _ViewerElementBase_environmentIntensity_accessor_storage, value, "f"); }
            /**
             * A value in radians that specifies the rotation of the environment.
             */
            get environmentRotation() { return __classPrivateFieldGet(this, _ViewerElementBase_environmentRotation_accessor_storage, "f"); }
            set environmentRotation(value) { __classPrivateFieldSet(this, _ViewerElementBase_environmentRotation_accessor_storage, value, "f"); }
            /**
             * The type of shadows to use.
             */
            get shadowQuality() { return __classPrivateFieldGet(this, _ViewerElementBase_shadowQuality_accessor_storage, "f"); }
            set shadowQuality(value) { __classPrivateFieldSet(this, _ViewerElementBase_shadowQuality_accessor_storage, value, "f"); }
            get _loadingProgress() { return __classPrivateFieldGet(this, _ViewerElementBase__loadingProgress_accessor_storage, "f"); }
            set _loadingProgress(value) { __classPrivateFieldSet(this, _ViewerElementBase__loadingProgress_accessor_storage, value, "f"); }
            /**
             * Gets information about loading activity.
             * @remarks
             * false indicates no loading activity.
             * true indicates loading activity with no progress information.
             * A number between 0 and 1 indicates loading activity with progress information.
             */
            get loadingProgress() {
                return this._loadingProgress;
            }
            /**
             * A value between 0 and 1 that specifies how much to blur the skybox.
             */
            get skyboxBlur() { return __classPrivateFieldGet(this, _ViewerElementBase_skyboxBlur_accessor_storage, "f"); }
            set skyboxBlur(value) { __classPrivateFieldSet(this, _ViewerElementBase_skyboxBlur_accessor_storage, value, "f"); }
            /**
             * The tone mapping to use for rendering the scene.
             */
            get toneMapping() { return __classPrivateFieldGet(this, _ViewerElementBase_toneMapping_accessor_storage, "f"); }
            set toneMapping(value) { __classPrivateFieldSet(this, _ViewerElementBase_toneMapping_accessor_storage, value, "f"); }
            /**
             * The contrast applied to the scene.
             */
            get contrast() { return __classPrivateFieldGet(this, _ViewerElementBase_contrast_accessor_storage, "f"); }
            set contrast(value) { __classPrivateFieldSet(this, _ViewerElementBase_contrast_accessor_storage, value, "f"); }
            /**
             * The exposure applied to the scene.
             */
            get exposure() { return __classPrivateFieldGet(this, _ViewerElementBase_exposure_accessor_storage, "f"); }
            set exposure(value) { __classPrivateFieldSet(this, _ViewerElementBase_exposure_accessor_storage, value, "f"); }
            /**
             * Enables or disables screen space ambient occlusion (SSAO).
             */
            get ssao() { return __classPrivateFieldGet(this, _ViewerElementBase_ssao_accessor_storage, "f"); }
            set ssao(value) { __classPrivateFieldSet(this, _ViewerElementBase_ssao_accessor_storage, value, "f"); }
            /**
             * The clear color (e.g. background color) for the viewer.
             */
            get clearColor() { return __classPrivateFieldGet(this, _ViewerElementBase_clearColor_accessor_storage, "f"); }
            set clearColor(value) { __classPrivateFieldSet(this, _ViewerElementBase_clearColor_accessor_storage, value, "f"); }
            /**
             * Enables or disables camera auto-orbit.
             */
            get cameraAutoOrbit() { return __classPrivateFieldGet(this, _ViewerElementBase_cameraAutoOrbit_accessor_storage, "f"); }
            set cameraAutoOrbit(value) { __classPrivateFieldSet(this, _ViewerElementBase_cameraAutoOrbit_accessor_storage, value, "f"); }
            /**
             * The speed at which the camera auto-orbits around the target.
             */
            get cameraAutoOrbitSpeed() { return __classPrivateFieldGet(this, _ViewerElementBase_cameraAutoOrbitSpeed_accessor_storage, "f"); }
            set cameraAutoOrbitSpeed(value) { __classPrivateFieldSet(this, _ViewerElementBase_cameraAutoOrbitSpeed_accessor_storage, value, "f"); }
            /**
             * The delay in milliseconds before the camera starts auto-orbiting.
             */
            get cameraAutoOrbitDelay() { return __classPrivateFieldGet(this, _ViewerElementBase_cameraAutoOrbitDelay_accessor_storage, "f"); }
            set cameraAutoOrbitDelay(value) { __classPrivateFieldSet(this, _ViewerElementBase_cameraAutoOrbitDelay_accessor_storage, value, "f"); }
            /**
             * The set of defined hot spots.
             */
            get hotSpots() { return __classPrivateFieldGet(this, _ViewerElementBase_hotSpots_accessor_storage, "f"); }
            set hotSpots(value) { __classPrivateFieldSet(this, _ViewerElementBase_hotSpots_accessor_storage, value, "f"); }
            /**
             * True if the viewer has any hotspots.
             */
            get _hasHotSpots() {
                return Object.keys(this.hotSpots).length > 0;
            }
            /**
             * True if the default animation should play automatically when a model is loaded.
             */
            get animationAutoPlay() { return __classPrivateFieldGet(this, _ViewerElementBase_animationAutoPlay_accessor_storage, "f"); }
            set animationAutoPlay(value) { __classPrivateFieldSet(this, _ViewerElementBase_animationAutoPlay_accessor_storage, value, "f"); }
            /**
             * The list of animation names for the currently loaded model.
             */
            get animations() {
                return this._animations;
            }
            /**
             * True if the loaded model has any animations.
             */
            get _hasAnimations() {
                return this._animations.length > 0;
            }
            /**
             * The currently selected animation index.
             */
            get selectedAnimation() { return __classPrivateFieldGet(this, _ViewerElementBase_selectedAnimation_accessor_storage, "f"); }
            set selectedAnimation(value) { __classPrivateFieldSet(this, _ViewerElementBase_selectedAnimation_accessor_storage, value, "f"); }
            /**
             * True if an animation is currently playing.
             */
            get isAnimationPlaying() {
                return this._isAnimationPlaying;
            }
            /**
             * The speed scale at which animations are played.
             */
            get animationSpeed() { return __classPrivateFieldGet(this, _ViewerElementBase_animationSpeed_accessor_storage, "f"); }
            set animationSpeed(value) { __classPrivateFieldSet(this, _ViewerElementBase_animationSpeed_accessor_storage, value, "f"); }
            /**
             * The current point on the selected animation timeline, normalized between 0 and 1.
             */
            get animationProgress() { return __classPrivateFieldGet(this, _ViewerElementBase_animationProgress_accessor_storage, "f"); }
            set animationProgress(value) { __classPrivateFieldSet(this, _ViewerElementBase_animationProgress_accessor_storage, value, "f"); }
            get _animations() { return __classPrivateFieldGet(this, _ViewerElementBase__animations_accessor_storage, "f"); }
            set _animations(value) { __classPrivateFieldSet(this, _ViewerElementBase__animations_accessor_storage, value, "f"); }
            get _isAnimationPlaying() { return __classPrivateFieldGet(this, _ViewerElementBase__isAnimationPlaying_accessor_storage, "f"); }
            set _isAnimationPlaying(value) { __classPrivateFieldSet(this, _ViewerElementBase__isAnimationPlaying_accessor_storage, value, "f"); }
            get _showAnimationSlider() { return __classPrivateFieldGet(this, _ViewerElementBase__showAnimationSlider_accessor_storage, "f"); }
            set _showAnimationSlider(value) { __classPrivateFieldSet(this, _ViewerElementBase__showAnimationSlider_accessor_storage, value, "f"); }
            /**
             * The list of material variants for the currently loaded model.
             */
            get materialVariants() {
                return this._viewer?.materialVariants ?? [];
            }
            /**
             * The currently selected material variant.
             */
            get selectedMaterialVariant() { return __classPrivateFieldGet(this, _ViewerElementBase_selectedMaterialVariant_accessor_storage, "f"); }
            set selectedMaterialVariant(value) { __classPrivateFieldSet(this, _ViewerElementBase_selectedMaterialVariant_accessor_storage, value, "f"); }
            /**
             * True if scene cameras should be used as hotspots.
             */
            get camerasAsHotSpots() { return __classPrivateFieldGet(this, _ViewerElementBase_camerasAsHotSpots_accessor_storage, "f"); }
            set camerasAsHotSpots(value) { __classPrivateFieldSet(this, _ViewerElementBase_camerasAsHotSpots_accessor_storage, value, "f"); }
            /**
             * Determines the behavior of the reset function, and the associated default reset button.
             * @remarks
             * - "auto" - Resets the camera to the initial pose if it makes sense given other viewer state, such as the selected animation.
             * - "reframe" - Reframes the camera based on the current viewer state (ignores the initial pose).
             * - [ResetFlag] - A space separated list of reset flags that reset various aspects of the viewer state.
             */
            get resetMode() { return __classPrivateFieldGet(this, _ViewerElementBase_resetMode_accessor_storage, "f"); }
            set resetMode(value) { __classPrivateFieldSet(this, _ViewerElementBase_resetMode_accessor_storage, value, "f"); }
            get _canvasContainer() { return __classPrivateFieldGet(this, _ViewerElementBase__canvasContainer_accessor_storage, "f"); }
            set _canvasContainer(value) { __classPrivateFieldSet(this, _ViewerElementBase__canvasContainer_accessor_storage, value, "f"); }
            get _hotSpotSelect() { return __classPrivateFieldGet(this, _ViewerElementBase__hotSpotSelect_accessor_storage, "f"); }
            set _hotSpotSelect(value) { __classPrivateFieldSet(this, _ViewerElementBase__hotSpotSelect_accessor_storage, value, "f"); }
            /**
             * Toggles the play/pause animation state if there is a selected animation.
             */
            toggleAnimation() {
                this._viewer?.toggleAnimation();
            }
            /**
             * Resets the Viewer state based on the @see resetMode property.
             */
            reset() {
                this._reset(this.resetMode);
            }
            _reset(mode) {
                switch (mode) {
                    case "auto":
                        this._viewer?.resetCamera(undefined);
                        break;
                    case "reframe":
                        this._viewer?.resetCamera(true);
                        break;
                    default:
                        this._viewer?.reset(...mode);
                        break;
                }
            }
            /**
             * Resets the camera to its initial pose.
             */
            resetCamera() {
                this._reset("reframe");
            }
            /**
             * Reloads the viewer. This is typically only needed when the viewer is in a faulted state (e.g. due to the context being lost).
             */
            reload() {
                this._tearDownViewer();
                this._setupViewer();
            }
            /** @internal */
            connectedCallback() {
                super.connectedCallback();
                this._setupViewer();
            }
            /** @internal */
            disconnectedCallback() {
                super.disconnectedCallback();
                this._tearDownViewer();
            }
            /** @internal */
            // eslint-disable-next-line @typescript-eslint/naming-convention
            attributeChangedCallback(name, oldValue, newValue) {
                super.attributeChangedCallback(name, oldValue, newValue);
                if (this.hasUpdated) {
                    if (name == "camera-orbit") {
                        const value = coerceCameraOrbitOrTarget(newValue);
                        if (value) {
                            this._viewer?.updateCamera({ alpha: value[0], beta: value[1], radius: value[2] });
                        }
                        else {
                            this._viewer?.resetCamera(false);
                        }
                    }
                    else if (name == "camera-target") {
                        const value = coerceCameraOrbitOrTarget(newValue);
                        if (value) {
                            this._viewer?.updateCamera({ targetX: value[0], targetY: value[1], targetZ: value[2] });
                        }
                        else {
                            this._viewer?.resetCamera(false);
                        }
                    }
                }
            }
            /** @internal */
            // eslint-disable-next-line @typescript-eslint/naming-convention
            update(changedProperties) {
                super.update(changedProperties);
                if (this._hotSpotSelect) {
                    this._hotSpotSelect.value = "";
                }
                if (this._needsReload(changedProperties)) {
                    this._tearDownViewer();
                    this._setupViewer();
                }
                else {
                    this._propertyBindings.filter((binding) => changedProperties.has(binding.property)).forEach((binding) => binding.updateViewer());
                    if (changedProperties.has("source") || changedProperties.has("useOpenPBR")) {
                        this._updateModel();
                    }
                    if (changedProperties.has("environmentLighting") || changedProperties.has("environmentSkybox")) {
                        this._updateEnv({
                            lighting: changedProperties.has("environmentLighting"),
                            skybox: changedProperties.has("environmentSkybox"),
                        });
                    }
                    if (changedProperties.has("shadowQuality")) {
                        this._updateShadows(this.shadowQuality);
                    }
                }
            }
            /**
             * Determines whether a full viewer reload is required for the given property changes.
             * Subclasses can override to add additional reload triggers.
             * @param changedProperties The properties that have changed.
             * @returns True if the viewer needs to be reloaded.
             */
            _needsReload(changedProperties) {
                return changedProperties.get("renderWhenIdle") != null;
            }
            /** @internal */
            // eslint-disable-next-line @typescript-eslint/naming-convention
            render() {
                return html `
            <div class="full-size">
                <div id="canvasContainer" class="full-size"></div>
                ${this._renderOverlay()}
            </div>
        `;
            }
            /**
             * Renders the progress bar.
             * @returns The template result for the progress bar.
             */
            _renderProgressBar() {
                const showProgressBar = this.loadingProgress !== false;
                // If loadingProgress is true, then the progress bar is indeterminate so the value doesn't matter.
                const progressValue = typeof this.loadingProgress === "boolean" ? 0 : this.loadingProgress * 100;
                const isIndeterminate = this.loadingProgress === true;
                return html `
            <div part="progress-bar" class="bar loading-progress-outer ${showProgressBar ? "" : "loading-progress-outer-inactive"}" aria-label="Loading Progress">
                <div
                    class="loading-progress-inner ${isIndeterminate ? "loading-progress-inner-indeterminate" : ""}"
                    style="${isIndeterminate ? "" : `width: ${progressValue}%`}"
                ></div>
            </div>
        `;
            }
            /**
             * Renders the toolbar.
             * @returns The template result for the toolbar.
             */
            _renderToolbar() {
                let toolbarControls = [];
                if (this._viewer?.isModelLoaded) {
                    // If the model has animations, add animation controls.
                    if (this._hasAnimations) {
                        toolbarControls.push(html `
                    <div class="animation-timeline">
                        <button aria-label="${this.isAnimationPlaying ? "Pause" : "Play"}" @click="${this.toggleAnimation}">
                            ${!this.isAnimationPlaying
                            ? html `
                                      <svg viewBox="0 0 24 24">
                                          <path d="${playFilledIcon}" fill="currentColor"></path>
                                      </svg>
                                  `
                            : html `
                                      <svg viewBox="0 0 24 24">
                                          <path d="${pauseFilledIcon}" fill="currentColor"></path>
                                      </svg>
                                  `}
                        </button>
                        <input
                            ${ref(this._onAnimationSliderChanged)}
                            aria-label="Animation Progress"
                            class="animation-timeline-input"
                            style="${this._showAnimationSlider ? "" : "visibility: hidden"}"
                            type="range"
                            min="0"
                            max="1"
                            step="0.0001"
                            .value="${this.animationProgress}"
                            @input="${this._onAnimationTimelineChanged}"
                            @pointerdown="${this._onAnimationTimelinePointerDown}"
                        />
                    </div>
                    <select aria-label="Select Animation Speed" @change="${this._onAnimationSpeedChanged}">
                        ${allowedAnimationSpeeds.map((speed) => html `<option value="${speed}" .selected="${this.animationSpeed === speed}">${speed}x</option> `)}
                    </select>
                    ${this.animations.length > 1
                            ? html `<select aria-label="Select Animation" @change="${this._onSelectedAnimationChanged}">
                              ${this.animations.map((name, index) => html `<option value="${index}" .selected="${this.selectedAnimation === index}">${name}</option>`)}
                          </select>`
                            : ""}
                `);
                    }
                    // If the model has material variants, add material variant controls.
                    if (this.materialVariants.length > 1) {
                        toolbarControls.push(html `
                    <select aria-label="Select Material Variant" @change="${this._onMaterialVariantChanged}">
                        ${this.materialVariants.map((name) => html `<option value="${name}" .selected="${this.selectedMaterialVariant === name}">${name}</option>`)}
                    </select>
                `);
                    }
                    // Always include a button to reset the camera pose.
                    toolbarControls.push(html `
                <button aria-label="Reset Camera Pose" @click="${this.reset}">
                    <svg viewBox="0 0 24 24">
                        <path d="${arrowResetFilledIcon}" fill="currentColor"></path>
                    </svg>
                </button>
            `);
                    // If hotspots have been defined, add hotspot controls.
                    if (this._hasHotSpots) {
                        toolbarControls.push(html `
                    <div class="select-container">
                        <select id="hotSpotSelect" aria-label="Select HotSpot" @change="${this._onHotSpotsChanged}">
                            <!-- When the select is forced to be less wide than the options, padding on the right is lost. Pad with white space. -->
                            ${Object.keys(this.hotSpots).map((name) => html `<option value="${name}">${name}&nbsp;&nbsp;</option>`)}
                        </select>
                        <!-- This button is not actually interactive, we want input to pass through to the select below. -->
                        <button style="pointer-events: none">
                            <svg viewBox="0 0 24 24">
                                <path d="${targetFilledIcon}" fill="currentColor"></path>
                            </svg>
                        </button>
                    </div>
                `);
                    }
                    // Add a vertical divider between each toolbar control.
                    const controlCount = toolbarControls.length;
                    const separator = html `<div class="divider"></div>`;
                    toolbarControls = toolbarControls.reduce((toolbarControls, toolbarControl, index) => {
                        if (index < controlCount - 1) {
                            return [...toolbarControls, toolbarControl, separator];
                        }
                        else {
                            return [...toolbarControls, toolbarControl];
                        }
                    }, new Array());
                }
                if (toolbarControls.length > 0) {
                    return html ` <div part="tool-bar" class="bar ${this._hasAnimations ? "" : "bar-min"} tool-bar">${toolbarControls}</div>`;
                }
                else {
                    return html ``;
                }
            }
            /**
             * Renders the reload button.
             * @returns The template result for the reload button.
             */
            _renderReloadButton() {
                return html `${this._isFaulted
                    ? html `
                  <button aria-label="Reload" part="reload-button" class="reload-button" @click="${this.reload}">
                      <svg viewBox="0 0 24 24">
                          <path d="${arrowClockwiseFilledIcon}" fill="currentColor"></path>
                      </svg>
                  </button>
              `
                    : ""}`;
            }
            /**
             * Renders UI elements that overlay the viewer.
             * Override this method to provide additional rendering for the component.
             * @returns TemplateResult The rendered template result.
             */
            _renderOverlay() {
                // NOTE: The unnamed 'slot' element holds all child elements of the <babylon-viewer> that do not specify a 'slot' attribute.
                return html `
            <slot class="full-size children-slot"></slot>
            <slot name="progress-bar">${this._renderProgressBar()}</slot>
            <slot name="tool-bar">${this._renderToolbar()}</slot>
            <slot name="reload-button">${this._renderReloadButton()}</slot>
        `;
            }
            /**
             * Dispatches a custom event.
             * @param type The type of the event.
             * @param event A function that creates the event.
             */
            _dispatchCustomEvent(type, event) {
                this.dispatchEvent(event(type));
            }
            /**
             * Handles changes to the selected animation.
             * @param event The change event.
             */
            _onSelectedAnimationChanged(event) {
                const selectElement = event.target;
                this.selectedAnimation = Number(selectElement.value);
            }
            /**
             * Handles changes to the animation speed.
             * @param event The change event.
             */
            _onAnimationSpeedChanged(event) {
                const selectElement = event.target;
                this.animationSpeed = Number(selectElement.value);
            }
            /**
             * Handles changes to the animation timeline.
             * @param event The change event.
             */
            _onAnimationTimelineChanged(event) {
                if (this._viewer) {
                    const input = event.target;
                    const value = Number(input.value);
                    if (value !== this.animationProgress) {
                        this._viewer.animationProgress = value;
                    }
                }
            }
            /**
             * Handles pointer down events on the animation timeline.
             * @param event The pointer down event.
             */
            _onAnimationTimelinePointerDown(event) {
                if (this._viewer?.isAnimationPlaying) {
                    this._viewer.pauseAnimation();
                    const input = event.target;
                    input.addEventListener("pointerup", () => this._viewer?.playAnimation(), { once: true });
                }
            }
            /**
             * Handles changes to the selected material variant.
             * @param event The change event.
             */
            _onMaterialVariantChanged(event) {
                const selectElement = event.target;
                this.selectedMaterialVariant = selectElement.value;
            }
            /**
             * Handles changes to the hot spot list.
             * @param event The change event.
             */
            _onHotSpotsChanged(event) {
                const selectElement = event.target;
                const hotSpotName = selectElement.value;
                // We don't actually want a selected value, this is just a one time trigger.
                selectElement.value = "";
                this.focusHotSpot(hotSpotName);
            }
            _onAnimationSliderChanged(element) {
                this._animationSliderResizeObserver?.disconnect();
                if (element) {
                    this._animationSliderResizeObserver = new ResizeObserver(() => {
                        this._showAnimationSlider = element.clientWidth >= 80;
                    });
                    this._animationSliderResizeObserver.observe(element);
                }
            }
            // Helper function to simplify keeping Viewer properties in sync with ViewerElementBase properties.
            _createPropertyBinding(property, getObservable, updateViewer, updateElement) {
                const tryUpdateViewer = (viewer) => {
                    try {
                        updateViewer(viewer);
                    }
                    catch (error) {
                        Logger.Error(error);
                    }
                };
                return {
                    property,
                    onInitialized: (viewer) => {
                        getObservable(viewer).add(() => {
                            updateElement(viewer);
                        });
                        tryUpdateViewer(viewer);
                    },
                    updateViewer: () => {
                        if (this._viewer) {
                            tryUpdateViewer(this._viewer);
                        }
                    },
                };
            }
            async _setupViewer() {
                await this._viewerLock.lockAsync(async () => {
                    // The first time the element is connected, the canvas container may not be available yet.
                    // Wait for the first update if needed.
                    if (!this._canvasContainer) {
                        await this.updateComplete;
                    }
                    if (this._canvasContainer && !this._viewer) {
                        const canvas = document.createElement("canvas");
                        canvas.className = "full-size canvas";
                        canvas.setAttribute("touch-action", "none");
                        this._canvasContainer.appendChild(canvas);
                        // Proxy intercepts option reads so that current HTML attribute values
                        // take precedence over the initial options passed to the constructor.
                        // eslint-disable-next-line @typescript-eslint/no-this-alias
                        const viewerElement = this;
                        const options = new Proxy(this._options, {
                            get(target, prop) {
                                switch (prop) {
                                    case "autoSuspendRendering":
                                        return !(viewerElement.hasAttribute("render-when-idle") || target.autoSuspendRendering === false);
                                    case "source":
                                        return viewerElement.getAttribute("source") ?? target.source;
                                    case "pluginExtension":
                                        return viewerElement.extension ?? target.pluginExtension;
                                    case "useOpenPBR":
                                        return viewerElement.hasAttribute("use-open-pbr") ? true : (viewerElement.useOpenPBR ?? target.useOpenPBR);
                                    case "environmentLighting":
                                        return viewerElement.getAttribute("environment-lighting") ?? viewerElement.getAttribute("environment") ?? target.environmentLighting;
                                    case "environmentSkybox":
                                        return viewerElement.getAttribute("environment-skybox") ?? viewerElement.getAttribute("environment") ?? target.environmentSkybox;
                                    case "environmentConfig":
                                        return {
                                            intensity: coerceNumericAttribute(viewerElement.getAttribute("environment-intensity")) ?? target.environmentConfig?.intensity,
                                            blur: coerceNumericAttribute(viewerElement.getAttribute("skybox-blur")) ?? target.environmentConfig?.blur,
                                            rotation: coerceNumericAttribute(viewerElement.getAttribute("environment-rotation")) ?? target.environmentConfig?.rotation,
                                        };
                                    case "shadowConfig":
                                        return {
                                            quality: coerceShadowQuality(viewerElement.getAttribute("shadow-quality")) ?? target.shadowConfig?.quality,
                                        };
                                    case "cameraOrbit":
                                        return coerceCameraOrbitOrTarget(viewerElement.getAttribute("camera-orbit")) ?? target.cameraOrbit;
                                    case "cameraTarget":
                                        return coerceCameraOrbitOrTarget(viewerElement.getAttribute("camera-target")) ?? target.cameraTarget;
                                    case "cameraAutoOrbit":
                                        return {
                                            enabled: viewerElement.hasAttribute("camera-auto-orbit") || target.cameraAutoOrbit?.enabled,
                                            speed: coerceNumericAttribute(viewerElement.getAttribute("camera-auto-orbit-speed")) ?? target.cameraAutoOrbit?.speed,
                                            delay: coerceNumericAttribute(viewerElement.getAttribute("camera-auto-orbit-delay")) ?? target.cameraAutoOrbit?.delay,
                                        };
                                    case "animationAutoPlay":
                                        return viewerElement.hasAttribute("animation-auto-play") || target.animationAutoPlay;
                                    case "animationSpeed":
                                        return coerceNumericAttribute(viewerElement.getAttribute("animation-speed")) ?? target.animationSpeed;
                                    case "selectedAnimation":
                                        return coerceNumericAttribute(viewerElement.getAttribute("selected-animation")) ?? target.selectedAnimation;
                                    case "postProcessing":
                                        return {
                                            toneMapping: coerceToneMapping(viewerElement.getAttribute("tone-mapping")) ?? target.postProcessing?.toneMapping,
                                            contrast: coerceNumericAttribute(viewerElement.getAttribute("contrast")) ?? target.postProcessing?.contrast,
                                            exposure: coerceNumericAttribute(viewerElement.getAttribute("exposure")) ?? target.postProcessing?.exposure,
                                            ssao: viewerElement.hasAttribute("ssao") || target.postProcessing?.ssao,
                                        };
                                    case "selectedMaterialVariant":
                                        return viewerElement.getAttribute("material-variant") ?? target.selectedMaterialVariant;
                                    case "onFaulted":
                                        return (error) => {
                                            viewerElement._isFaultedBacking = true;
                                            target.onFaulted?.(error);
                                            viewerElement._tearDownViewer();
                                        };
                                    default:
                                        return target[prop];
                                }
                            },
                        });
                        const viewer = await this._createViewer(canvas, options);
                        this._viewer = viewer;
                        viewer.onEnvironmentChanged.add(() => {
                            this._dispatchCustomEvent("environmentchange", (type) => new Event(type));
                        });
                        viewer.onEnvironmentConfigurationChanged.add(() => {
                            this._dispatchCustomEvent("environmentconfigurationchange", (type) => new Event(type));
                        });
                        viewer.onEnvironmentError.add((error) => {
                            this._dispatchCustomEvent("environmenterror", (type) => new ErrorEvent(type, { error }));
                        });
                        viewer.onShadowsConfigurationChanged.add(() => {
                            this._dispatchCustomEvent("shadowsconfigurationchange", (type) => new Event(type));
                        });
                        viewer.onModelChanged.add((source) => {
                            this._animations = [...viewer.animations];
                            this._dispatchCustomEvent("modelchange", (type) => new CustomEvent(type, { detail: source }));
                        });
                        viewer.onModelError.add((error) => {
                            this._animations = [...viewer.animations];
                            this._dispatchCustomEvent("modelerror", (type) => new ErrorEvent(type, { error }));
                        });
                        viewer.onLoadingProgressChanged.add(() => {
                            this._loadingProgress = viewer.loadingProgress;
                            this._dispatchCustomEvent("loadingprogresschange", (type) => new Event(type));
                        });
                        viewer.onSelectedAnimationChanged.add(() => {
                            this._dispatchCustomEvent("selectedanimationchange", (type) => new Event(type));
                        });
                        viewer.onIsAnimationPlayingChanged.add(() => {
                            this._isAnimationPlaying = viewer.isAnimationPlaying ?? false;
                            this._dispatchCustomEvent("animationplayingchange", (type) => new Event(type));
                        });
                        viewer.onAnimationProgressChanged.add(() => {
                            this.animationProgress = viewer.animationProgress ?? 0;
                            this._dispatchCustomEvent("animationprogresschange", (type) => new Event(type));
                        });
                        viewer.onSelectedMaterialVariantChanged.add(() => {
                            this._dispatchCustomEvent("selectedmaterialvariantchange", (type) => new Event(type));
                        });
                        viewer.onAfterRenderObservable.add(() => {
                            this._dispatchCustomEvent("viewerrender", (type) => new Event(type));
                        });
                        this._propertyBindings.forEach((binding) => binding.onInitialized(viewer));
                        this._dispatchCustomEvent("viewerready", (type) => new Event(type));
                    }
                    this._isFaultedBacking = false;
                });
            }
            async _tearDownViewer() {
                await this._viewerLock.lockAsync(async () => {
                    if (this._viewer) {
                        this._viewer.dispose();
                        this._viewer = undefined;
                    }
                    this._onViewerTornDown();
                    this._loadingProgress = false;
                    // We want to replace the canvas for two reasons:
                    // 1. When the viewer element is reconnected to the DOM, we don't want to briefly see the last frame of the previous model.
                    // 2. If we are changing engines (e.g. WebGL to WebGPU), we need to create a new canvas for the new engine.
                    if (this._canvasContainer && this._canvasContainer.firstElementChild) {
                        this._canvasContainer.removeChild(this._canvasContainer.firstElementChild);
                    }
                });
            }
            /**
             * Called during teardown after the viewer has been disposed.
             * Subclasses can override to clean up additional state.
             */
            _onViewerTornDown() {
                // Intentionally empty — subclasses override as needed.
            }
            async _updateModel() {
                if (this._viewer) {
                    try {
                        if (this.source) {
                            await this._viewer.loadModel(this.source, {
                                pluginExtension: this.extension ?? undefined,
                                useOpenPBR: this.useOpenPBR,
                            });
                        }
                        else {
                            await this._viewer.resetModel();
                        }
                    }
                    catch (error) {
                        // If loadModel was aborted (e.g. because a new model load was requested before this one finished), we can just ignore the error.
                        if (!(error instanceof AbortError)) {
                            Logger.Error(error);
                        }
                    }
                }
            }
            async _updateEnv(options) {
                if (this._viewer) {
                    try {
                        const updates = [];
                        if (options.lighting && options.skybox && this.environmentLighting === this.environmentSkybox) {
                            updates.push([this.environmentLighting, { lighting: true, skybox: true }]);
                        }
                        else {
                            if (options.lighting) {
                                updates.push([this.environmentLighting, { lighting: true }]);
                            }
                            if (options.skybox) {
                                updates.push([this.environmentSkybox, { skybox: true }]);
                            }
                        }
                        const promises = updates.map(async ([url, options]) => {
                            if (url) {
                                await this._viewer?.loadEnvironment(url, options);
                            }
                            else {
                                await this._viewer?.resetEnvironment(options);
                            }
                        });
                        await Promise.all(promises);
                    }
                    catch (error) {
                        // If loadEnvironment was aborted (e.g. because a new environment load was requested before this one finished), we can just ignore the error.
                        if (!(error instanceof AbortError)) {
                            Logger.Error(error);
                        }
                    }
                }
            }
            async _updateShadows(quality) {
                if (!quality) {
                    return;
                }
                try {
                    const options = { quality };
                    await this._viewer?.updateShadows(options);
                }
                catch (error) {
                    // If loadEnvironment was aborted (e.g. because a new environment load was requested before this one finished), we can just ignore the error.
                    if (!(error instanceof AbortError)) {
                        Logger.Error(error);
                    }
                }
            }
        },
        _ViewerElementBase__isFaultedBacking_accessor_storage = new WeakMap(),
        _ViewerElementBase_renderWhenIdle_accessor_storage = new WeakMap(),
        _ViewerElementBase_source_accessor_storage = new WeakMap(),
        _ViewerElementBase_extension_accessor_storage = new WeakMap(),
        _ViewerElementBase_useOpenPBR_accessor_storage = new WeakMap(),
        _ViewerElementBase_environmentLighting_accessor_storage = new WeakMap(),
        _ViewerElementBase_environmentSkybox_accessor_storage = new WeakMap(),
        _ViewerElementBase_environmentIntensity_accessor_storage = new WeakMap(),
        _ViewerElementBase_environmentRotation_accessor_storage = new WeakMap(),
        _ViewerElementBase_shadowQuality_accessor_storage = new WeakMap(),
        _ViewerElementBase__loadingProgress_accessor_storage = new WeakMap(),
        _ViewerElementBase_skyboxBlur_accessor_storage = new WeakMap(),
        _ViewerElementBase_toneMapping_accessor_storage = new WeakMap(),
        _ViewerElementBase_contrast_accessor_storage = new WeakMap(),
        _ViewerElementBase_exposure_accessor_storage = new WeakMap(),
        _ViewerElementBase_ssao_accessor_storage = new WeakMap(),
        _ViewerElementBase_clearColor_accessor_storage = new WeakMap(),
        _ViewerElementBase_cameraAutoOrbit_accessor_storage = new WeakMap(),
        _ViewerElementBase_cameraAutoOrbitSpeed_accessor_storage = new WeakMap(),
        _ViewerElementBase_cameraAutoOrbitDelay_accessor_storage = new WeakMap(),
        _ViewerElementBase_hotSpots_accessor_storage = new WeakMap(),
        _ViewerElementBase_animationAutoPlay_accessor_storage = new WeakMap(),
        _ViewerElementBase_selectedAnimation_accessor_storage = new WeakMap(),
        _ViewerElementBase_animationSpeed_accessor_storage = new WeakMap(),
        _ViewerElementBase_animationProgress_accessor_storage = new WeakMap(),
        _ViewerElementBase__animations_accessor_storage = new WeakMap(),
        _ViewerElementBase__isAnimationPlaying_accessor_storage = new WeakMap(),
        _ViewerElementBase__showAnimationSlider_accessor_storage = new WeakMap(),
        _ViewerElementBase_selectedMaterialVariant_accessor_storage = new WeakMap(),
        _ViewerElementBase_camerasAsHotSpots_accessor_storage = new WeakMap(),
        _ViewerElementBase_resetMode_accessor_storage = new WeakMap(),
        _ViewerElementBase__canvasContainer_accessor_storage = new WeakMap(),
        _ViewerElementBase__hotSpotSelect_accessor_storage = new WeakMap(),
        (() => {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(_classSuper[Symbol.metadata] ?? null) : void 0;
            __isFaultedBacking_decorators = [state()];
            _renderWhenIdle_decorators = [property({ attribute: "render-when-idle", type: Boolean })];
            _source_decorators = [property()];
            _extension_decorators = [property()];
            _useOpenPBR_decorators = [property({ attribute: "use-open-pbr", type: Boolean })];
            _set_environment_decorators = [property({
                    hasChanged: (newValue, oldValue) => {
                        const environmentUrl = newValue || null;
                        return environmentUrl !== oldValue.lighting || environmentUrl !== oldValue.skybox;
                    },
                })];
            _environmentLighting_decorators = [property({ attribute: "environment-lighting" })];
            _environmentSkybox_decorators = [property({ attribute: "environment-skybox" })];
            _environmentIntensity_decorators = [property({ type: Number, attribute: "environment-intensity" })];
            _environmentRotation_decorators = [property({
                    type: Number,
                    attribute: "environment-rotation",
                })];
            _shadowQuality_decorators = [property({
                    attribute: "shadow-quality",
                })];
            __loadingProgress_decorators = [state()];
            _skyboxBlur_decorators = [property({ attribute: "skybox-blur" })];
            _toneMapping_decorators = [property({
                    attribute: "tone-mapping",
                    converter: (value) => {
                        if (!value || !IsToneMapping(value)) {
                            return "neutral";
                        }
                        return value;
                    },
                })];
            _contrast_decorators = [property()];
            _exposure_decorators = [property()];
            _ssao_decorators = [property({ type: String })];
            _clearColor_decorators = [property({
                    attribute: "clear-color",
                    converter: {
                        fromAttribute: parseColor,
                        toAttribute: (color) => (color ? colorToHex(color) : null),
                    },
                })];
            _cameraAutoOrbit_decorators = [property({
                    attribute: "camera-auto-orbit",
                    type: Boolean,
                })];
            _cameraAutoOrbitSpeed_decorators = [property({
                    attribute: "camera-auto-orbit-speed",
                    type: Number,
                })];
            _cameraAutoOrbitDelay_decorators = [property({
                    attribute: "camera-auto-orbit-delay",
                    type: Number,
                })];
            _hotSpots_decorators = [property({
                    attribute: "hotspots",
                    converter: (value) => {
                        if (!value) {
                            return {};
                        }
                        return JSON.parse(value);
                    },
                })];
            _animationAutoPlay_decorators = [property({ attribute: "animation-auto-play", type: Boolean })];
            _selectedAnimation_decorators = [property({ attribute: "selected-animation", type: Number })];
            _animationSpeed_decorators = [property({ attribute: "animation-speed" })];
            _animationProgress_decorators = [property({ attribute: false })];
            __animations_decorators = [state()];
            __isAnimationPlaying_decorators = [state()];
            __showAnimationSlider_decorators = [state()];
            _selectedMaterialVariant_decorators = [property({ attribute: "material-variant" })];
            _camerasAsHotSpots_decorators = [property({ attribute: "cameras-as-hotspots", type: Boolean })];
            _resetMode_decorators = [property({ attribute: "reset-mode", converter: coerceResetMode })];
            __canvasContainer_decorators = [query("#canvasContainer")];
            __hotSpotSelect_decorators = [query("#hotSpotSelect")];
            __esDecorate(_a, null, __isFaultedBacking_decorators, { kind: "accessor", name: "_isFaultedBacking", static: false, private: false, access: { has: obj => "_isFaultedBacking" in obj, get: obj => obj._isFaultedBacking, set: (obj, value) => { obj._isFaultedBacking = value; } }, metadata: _metadata }, __isFaultedBacking_initializers, __isFaultedBacking_extraInitializers);
            __esDecorate(_a, null, _renderWhenIdle_decorators, { kind: "accessor", name: "renderWhenIdle", static: false, private: false, access: { has: obj => "renderWhenIdle" in obj, get: obj => obj.renderWhenIdle, set: (obj, value) => { obj.renderWhenIdle = value; } }, metadata: _metadata }, _renderWhenIdle_initializers, _renderWhenIdle_extraInitializers);
            __esDecorate(_a, null, _source_decorators, { kind: "accessor", name: "source", static: false, private: false, access: { has: obj => "source" in obj, get: obj => obj.source, set: (obj, value) => { obj.source = value; } }, metadata: _metadata }, _source_initializers, _source_extraInitializers);
            __esDecorate(_a, null, _extension_decorators, { kind: "accessor", name: "extension", static: false, private: false, access: { has: obj => "extension" in obj, get: obj => obj.extension, set: (obj, value) => { obj.extension = value; } }, metadata: _metadata }, _extension_initializers, _extension_extraInitializers);
            __esDecorate(_a, null, _useOpenPBR_decorators, { kind: "accessor", name: "useOpenPBR", static: false, private: false, access: { has: obj => "useOpenPBR" in obj, get: obj => obj.useOpenPBR, set: (obj, value) => { obj.useOpenPBR = value; } }, metadata: _metadata }, _useOpenPBR_initializers, _useOpenPBR_extraInitializers);
            __esDecorate(_a, null, _set_environment_decorators, { kind: "setter", name: "environment", static: false, private: false, access: { has: obj => "environment" in obj, set: (obj, value) => { obj.environment = value; } }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(_a, null, _environmentLighting_decorators, { kind: "accessor", name: "environmentLighting", static: false, private: false, access: { has: obj => "environmentLighting" in obj, get: obj => obj.environmentLighting, set: (obj, value) => { obj.environmentLighting = value; } }, metadata: _metadata }, _environmentLighting_initializers, _environmentLighting_extraInitializers);
            __esDecorate(_a, null, _environmentSkybox_decorators, { kind: "accessor", name: "environmentSkybox", static: false, private: false, access: { has: obj => "environmentSkybox" in obj, get: obj => obj.environmentSkybox, set: (obj, value) => { obj.environmentSkybox = value; } }, metadata: _metadata }, _environmentSkybox_initializers, _environmentSkybox_extraInitializers);
            __esDecorate(_a, null, _environmentIntensity_decorators, { kind: "accessor", name: "environmentIntensity", static: false, private: false, access: { has: obj => "environmentIntensity" in obj, get: obj => obj.environmentIntensity, set: (obj, value) => { obj.environmentIntensity = value; } }, metadata: _metadata }, _environmentIntensity_initializers, _environmentIntensity_extraInitializers);
            __esDecorate(_a, null, _environmentRotation_decorators, { kind: "accessor", name: "environmentRotation", static: false, private: false, access: { has: obj => "environmentRotation" in obj, get: obj => obj.environmentRotation, set: (obj, value) => { obj.environmentRotation = value; } }, metadata: _metadata }, _environmentRotation_initializers, _environmentRotation_extraInitializers);
            __esDecorate(_a, null, _shadowQuality_decorators, { kind: "accessor", name: "shadowQuality", static: false, private: false, access: { has: obj => "shadowQuality" in obj, get: obj => obj.shadowQuality, set: (obj, value) => { obj.shadowQuality = value; } }, metadata: _metadata }, _shadowQuality_initializers, _shadowQuality_extraInitializers);
            __esDecorate(_a, null, __loadingProgress_decorators, { kind: "accessor", name: "_loadingProgress", static: false, private: false, access: { has: obj => "_loadingProgress" in obj, get: obj => obj._loadingProgress, set: (obj, value) => { obj._loadingProgress = value; } }, metadata: _metadata }, __loadingProgress_initializers, __loadingProgress_extraInitializers);
            __esDecorate(_a, null, _skyboxBlur_decorators, { kind: "accessor", name: "skyboxBlur", static: false, private: false, access: { has: obj => "skyboxBlur" in obj, get: obj => obj.skyboxBlur, set: (obj, value) => { obj.skyboxBlur = value; } }, metadata: _metadata }, _skyboxBlur_initializers, _skyboxBlur_extraInitializers);
            __esDecorate(_a, null, _toneMapping_decorators, { kind: "accessor", name: "toneMapping", static: false, private: false, access: { has: obj => "toneMapping" in obj, get: obj => obj.toneMapping, set: (obj, value) => { obj.toneMapping = value; } }, metadata: _metadata }, _toneMapping_initializers, _toneMapping_extraInitializers);
            __esDecorate(_a, null, _contrast_decorators, { kind: "accessor", name: "contrast", static: false, private: false, access: { has: obj => "contrast" in obj, get: obj => obj.contrast, set: (obj, value) => { obj.contrast = value; } }, metadata: _metadata }, _contrast_initializers, _contrast_extraInitializers);
            __esDecorate(_a, null, _exposure_decorators, { kind: "accessor", name: "exposure", static: false, private: false, access: { has: obj => "exposure" in obj, get: obj => obj.exposure, set: (obj, value) => { obj.exposure = value; } }, metadata: _metadata }, _exposure_initializers, _exposure_extraInitializers);
            __esDecorate(_a, null, _ssao_decorators, { kind: "accessor", name: "ssao", static: false, private: false, access: { has: obj => "ssao" in obj, get: obj => obj.ssao, set: (obj, value) => { obj.ssao = value; } }, metadata: _metadata }, _ssao_initializers, _ssao_extraInitializers);
            __esDecorate(_a, null, _clearColor_decorators, { kind: "accessor", name: "clearColor", static: false, private: false, access: { has: obj => "clearColor" in obj, get: obj => obj.clearColor, set: (obj, value) => { obj.clearColor = value; } }, metadata: _metadata }, _clearColor_initializers, _clearColor_extraInitializers);
            __esDecorate(_a, null, _cameraAutoOrbit_decorators, { kind: "accessor", name: "cameraAutoOrbit", static: false, private: false, access: { has: obj => "cameraAutoOrbit" in obj, get: obj => obj.cameraAutoOrbit, set: (obj, value) => { obj.cameraAutoOrbit = value; } }, metadata: _metadata }, _cameraAutoOrbit_initializers, _cameraAutoOrbit_extraInitializers);
            __esDecorate(_a, null, _cameraAutoOrbitSpeed_decorators, { kind: "accessor", name: "cameraAutoOrbitSpeed", static: false, private: false, access: { has: obj => "cameraAutoOrbitSpeed" in obj, get: obj => obj.cameraAutoOrbitSpeed, set: (obj, value) => { obj.cameraAutoOrbitSpeed = value; } }, metadata: _metadata }, _cameraAutoOrbitSpeed_initializers, _cameraAutoOrbitSpeed_extraInitializers);
            __esDecorate(_a, null, _cameraAutoOrbitDelay_decorators, { kind: "accessor", name: "cameraAutoOrbitDelay", static: false, private: false, access: { has: obj => "cameraAutoOrbitDelay" in obj, get: obj => obj.cameraAutoOrbitDelay, set: (obj, value) => { obj.cameraAutoOrbitDelay = value; } }, metadata: _metadata }, _cameraAutoOrbitDelay_initializers, _cameraAutoOrbitDelay_extraInitializers);
            __esDecorate(_a, null, _hotSpots_decorators, { kind: "accessor", name: "hotSpots", static: false, private: false, access: { has: obj => "hotSpots" in obj, get: obj => obj.hotSpots, set: (obj, value) => { obj.hotSpots = value; } }, metadata: _metadata }, _hotSpots_initializers, _hotSpots_extraInitializers);
            __esDecorate(_a, null, _animationAutoPlay_decorators, { kind: "accessor", name: "animationAutoPlay", static: false, private: false, access: { has: obj => "animationAutoPlay" in obj, get: obj => obj.animationAutoPlay, set: (obj, value) => { obj.animationAutoPlay = value; } }, metadata: _metadata }, _animationAutoPlay_initializers, _animationAutoPlay_extraInitializers);
            __esDecorate(_a, null, _selectedAnimation_decorators, { kind: "accessor", name: "selectedAnimation", static: false, private: false, access: { has: obj => "selectedAnimation" in obj, get: obj => obj.selectedAnimation, set: (obj, value) => { obj.selectedAnimation = value; } }, metadata: _metadata }, _selectedAnimation_initializers, _selectedAnimation_extraInitializers);
            __esDecorate(_a, null, _animationSpeed_decorators, { kind: "accessor", name: "animationSpeed", static: false, private: false, access: { has: obj => "animationSpeed" in obj, get: obj => obj.animationSpeed, set: (obj, value) => { obj.animationSpeed = value; } }, metadata: _metadata }, _animationSpeed_initializers, _animationSpeed_extraInitializers);
            __esDecorate(_a, null, _animationProgress_decorators, { kind: "accessor", name: "animationProgress", static: false, private: false, access: { has: obj => "animationProgress" in obj, get: obj => obj.animationProgress, set: (obj, value) => { obj.animationProgress = value; } }, metadata: _metadata }, _animationProgress_initializers, _animationProgress_extraInitializers);
            __esDecorate(_a, null, __animations_decorators, { kind: "accessor", name: "_animations", static: false, private: false, access: { has: obj => "_animations" in obj, get: obj => obj._animations, set: (obj, value) => { obj._animations = value; } }, metadata: _metadata }, __animations_initializers, __animations_extraInitializers);
            __esDecorate(_a, null, __isAnimationPlaying_decorators, { kind: "accessor", name: "_isAnimationPlaying", static: false, private: false, access: { has: obj => "_isAnimationPlaying" in obj, get: obj => obj._isAnimationPlaying, set: (obj, value) => { obj._isAnimationPlaying = value; } }, metadata: _metadata }, __isAnimationPlaying_initializers, __isAnimationPlaying_extraInitializers);
            __esDecorate(_a, null, __showAnimationSlider_decorators, { kind: "accessor", name: "_showAnimationSlider", static: false, private: false, access: { has: obj => "_showAnimationSlider" in obj, get: obj => obj._showAnimationSlider, set: (obj, value) => { obj._showAnimationSlider = value; } }, metadata: _metadata }, __showAnimationSlider_initializers, __showAnimationSlider_extraInitializers);
            __esDecorate(_a, null, _selectedMaterialVariant_decorators, { kind: "accessor", name: "selectedMaterialVariant", static: false, private: false, access: { has: obj => "selectedMaterialVariant" in obj, get: obj => obj.selectedMaterialVariant, set: (obj, value) => { obj.selectedMaterialVariant = value; } }, metadata: _metadata }, _selectedMaterialVariant_initializers, _selectedMaterialVariant_extraInitializers);
            __esDecorate(_a, null, _camerasAsHotSpots_decorators, { kind: "accessor", name: "camerasAsHotSpots", static: false, private: false, access: { has: obj => "camerasAsHotSpots" in obj, get: obj => obj.camerasAsHotSpots, set: (obj, value) => { obj.camerasAsHotSpots = value; } }, metadata: _metadata }, _camerasAsHotSpots_initializers, _camerasAsHotSpots_extraInitializers);
            __esDecorate(_a, null, _resetMode_decorators, { kind: "accessor", name: "resetMode", static: false, private: false, access: { has: obj => "resetMode" in obj, get: obj => obj.resetMode, set: (obj, value) => { obj.resetMode = value; } }, metadata: _metadata }, _resetMode_initializers, _resetMode_extraInitializers);
            __esDecorate(_a, null, __canvasContainer_decorators, { kind: "accessor", name: "_canvasContainer", static: false, private: false, access: { has: obj => "_canvasContainer" in obj, get: obj => obj._canvasContainer, set: (obj, value) => { obj._canvasContainer = value; } }, metadata: _metadata }, __canvasContainer_initializers, __canvasContainer_extraInitializers);
            __esDecorate(_a, null, __hotSpotSelect_decorators, { kind: "accessor", name: "_hotSpotSelect", static: false, private: false, access: { has: obj => "_hotSpotSelect" in obj, get: obj => obj._hotSpotSelect, set: (obj, value) => { obj._hotSpotSelect = value; } }, metadata: _metadata }, __hotSpotSelect_initializers, __hotSpotSelect_extraInitializers);
            if (_metadata) Object.defineProperty(_a, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
        })(),
        /** @internal */
        // eslint-disable-next-line @typescript-eslint/naming-convention
        _a.styles = css `
        :host {
            --ui-foreground-color: white;
            --ui-background-hue: 233;
            --ui-background-saturation: 8%;
            --ui-background-lightness: 39%;
            --ui-background-opacity: 0.75;
            --ui-background-color: hsla(var(--ui-background-hue), var(--ui-background-saturation), var(--ui-background-lightness), var(--ui-background-opacity));
            --ui-background-color-hover: hsla(
                var(--ui-background-hue),
                var(--ui-background-saturation),
                calc(var(--ui-background-lightness) - 10%),
                calc(var(--ui-background-opacity) - 0.1)
            );
            all: inherit;
            overflow: hidden;
        }

        .full-size {
            display: block;
            position: relative;
            width: 100%;
            height: 100%;
        }

        .canvas {
            outline: none;
        }

        .children-slot {
            position: absolute;
            top: 0;
            background: transparent;
            pointer-events: none;
        }

        .reload-button {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 25%;
            transform: translate(-50%, -50%);
            color: var(--ui-foreground-color);
            background-color: var(--ui-background-color);
            border: 1px solid transparent;
            border-radius: 24px;
            padding: 0;
            cursor: pointer;
            outline: none;
        }

        .reload-button:hover {
            background-color: var(--ui-background-color-hover);
        }

        .bar {
            position: absolute;
            width: calc(100% - 24px);
            min-width: 370px;
            max-width: 1280px;
            left: 50%;
            transform: translateX(-50%);
            background-color: var(--ui-background-color);
        }

        .bar-min {
            width: unset;
            min-width: unset;
            max-width: unset;
        }

        .loading-progress-outer {
            height: 4px;
            border-radius: 4px;
            border: 1px solid var(--ui-background-color);
            outline: none;
            top: 12px;
            pointer-events: none;
            transition: opacity 0.5s ease;
        }

        .loading-progress-outer-inactive {
            opacity: 0;
            /* Set the background color to the foreground color while in the inactive state so that the color seen is correct while fading out the opacity. */
            background-color: var(--ui-foreground-color);
        }

        .loading-progress-inner {
            width: 0;
            height: 100%;
            border-radius: inherit;
            background-color: var(--ui-foreground-color);
            transition: width 0.3s linear;
        }

        /* The right side of the inner progress bar starts aligned with the left side of the outer progress bar (container).
           So, if the width is 30%, then the left side of the inner progress bar moves a total of 130% of the width of the container.
           This is why the first keyframe is at 23% ((100/130)*30).
         */
        @keyframes indeterminate {
            0% {
                left: 0%;
                width: 0%;
            }
            23% {
                left: 0%;
                width: 30%;
            }
            77% {
                left: 70%;
                width: 30%;
            }
            100% {
                left: 100%;
                width: 0%;
            }
        }

        .loading-progress-inner-indeterminate {
            position: absolute;
            animation: indeterminate 1.5s infinite;
            animation-timing-function: linear;
        }

        .tool-bar {
            display: flex;
            flex-direction: row;
            align-items: center;
            border-radius: 12px;
            border-color: var(--ui-foreground-color);
            height: 48px;
            bottom: 12px;
            color: var(--ui-foreground-color);
            -webkit-tap-highlight-color: transparent;
        }

        .tool-bar * {
            height: 100%;
            min-width: 48px;
        }

        .tool-bar .divider {
            min-width: 1px;
            margin: 0px 6px;
            height: 66%;
            background-color: var(--ui-foreground-color);
        }

        .tool-bar select {
            background: none;
            min-width: 52px;
            max-width: 128px;
            border: 1px solid transparent;
            border-radius: inherit;
            color: inherit;
            font-size: 14px;
            padding: 0px 12px;
            cursor: pointer;
            outline: none;
            appearance: none; /* Remove default styling */
            -webkit-appearance: none; /* Remove default styling for Safari */
        }

        .tool-bar .select-container {
            position: relative;
            display: flex;
            border-radius: inherit;
            border-width: 0;
            padding: 0;
        }

        .tool-bar .select-container select {
            position: absolute;
            min-width: 0;
            width: 100%;
        }

        .tool-bar .select-container button {
            position: absolute;
            border-width: 0;
        }

        .tool-bar select:hover,
        .tool-bar select:focus {
            background-color: var(--ui-background-color-hover);
        }

        .tool-bar select option {
            background-color: var(--ui-background-color);
            color: var(--ui-foreground-color);
        }

        .tool-bar select:focus-visible {
            border-color: inherit;
        }

        .tool-bar button {
            background: none;
            border: 1px solid transparent;
            border-radius: inherit;
            color: inherit;
            padding: 0;
            cursor: pointer;
            outline: none;
        }

        .tool-bar button:hover {
            background-color: var(--ui-background-color-hover);
        }

        .tool-bar button:focus-visible {
            border-color: inherit;
        }

        .tool-bar button svg {
            width: 32px;
            height: 32px;
        }

        .animation-timeline {
            display: flex;
            flex: 1;
            position: relative;
            overflow: hidden;
            cursor: pointer;
            align-items: center;
            border-radius: inherit;
            border-color: inherit;
        }

        .animation-timeline-input {
            -webkit-appearance: none;
            cursor: pointer;
            width: 100%;
            height: 100%;
            outline: none;
            border: 1px solid transparent;
            border-radius: inherit;
            padding: 0 12px;
            background-color: transparent;
        }

        .animation-timeline-input:focus-visible {
            border-color: inherit;
        }

        /*Chrome -webkit */

        .animation-timeline-input::-webkit-slider-thumb {
            -webkit-appearance: none;
            width: 20px;
            height: 20px;
            border: 2px solid;
            color: var(--ui-foreground-color);
            border-radius: 50%;
            background: hsla(var(--ui-background-hue), var(--ui-background-saturation), var(--ui-background-lightness), 1);
            margin-top: -10px;
        }

        .animation-timeline-input::-webkit-slider-runnable-track {
            height: 2px;
            -webkit-appearance: none;
            background-color: var(--ui-foreground-color);
        }

        /** FireFox -moz */

        .animation-timeline-input::-moz-range-progress {
            height: 2px;
            background-color: var(--ui-foreground-color);
        }

        .animation-timeline-input::-moz-range-thumb {
            width: 16px;
            height: 16px;
            border: 2px solid var(--ui-foreground-color);
            border-radius: 50%;
            background: hsla(var(--ui-background-hue), var(--ui-background-saturation), var(--ui-background-lightness), 1);
        }

        .animation-timeline-input::-moz-range-track {
            height: 2px;
            background: var(--ui-foreground-color);
        }
    `,
        _a;
})();
export { ViewerElementBase };
//# sourceMappingURL=viewerElementBase.js.map